import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    Chip,
    IconButton,
    InputAdornment,
    Autocomplete,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Login as ArriveIcon,
    Logout as DepartIcon,
    Assignment as InspectionIcon,
    AddLocationAlt as AddLocationIcon,
    CloudOff as OfflineIcon,
    ExpandMore as ExpandMoreIcon,
    CloudDone as SentIcon,
    CloudQueue as QueuedIcon,
    ErrorOutline as NotSentIcon,
    Sync as SyncIcon,
    PhotoCamera as PhotoIcon,
    EditNote as DailyNoteIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Groups as GroupViewIcon,
    UploadFile as DocumentIcon,
    EditOutlined as EditIcon,
    DeleteOutline as RemoveIcon,
    AttachFile as FilesIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import safetyScheduleService, { MAX_FILES_PER_VISIT, MAX_DOCUMENT_BYTES } from '../../services/safetyScheduleService';
import { initializeDB } from '../../config/db';
import { JOB_STORE, ALT_JOB_STORE } from '../../config/constants';
import { buildLocationOptions } from './utils/locationUtils';
import {
    DOW,
    pad,
    parseDay,
    todayKey,
    weekDays,
    weekEndingOf,
    weekLabel,
    orderWeeks,
    weekFromQuery,
} from './utils/scheduleDates';
import { dayTitle, pickLocation } from './schedule/visitUtils';
import VisitEditDialog from './schedule/VisitEditDialog';
import VisitFilesDialog from './schedule/VisitFilesDialog';
import CameraCapture from '../shared/CameraCapture';
import useDictation from '../../hooks/useDictation';

/**
 * Снимок уменьшаем перед очередью, как в форме инспекции: полноразмерное фото с
 * iPad — это мегабайты base64 в IndexedDB и в запросе с площадки, где связь
 * еле жива. 1600 пикселей по длинной стороне для фото площадки хватает.
 */
function downscalePhoto(dataUrl, maxDim = 1600, quality = 0.8) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        // Браузер не смог прочитать картинку — отправляем как есть, сервер проверит.
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Could not read the file'));
        reader.readAsDataURL(file);
    });
}

function nowTime() {
    const now = new Date();
    return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function formatTime(value) {
    if (!value) return null;
    const [h, m] = value.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 === 0 ? 12 : h % 12}:${pad(m)} ${suffix}`;
}

const SafetySchedulePage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState(0);
    const [online, setOnline] = useState(navigator.onLine);
    const [locations, setLocations] = useState([]);
    const [meta, setMeta] = useState(null);
    // Неделя живёт и в адресе: из Group View человек возвращается на ту же
    // неделю, которую смотрел, а не на текущую.
    const [week, setWeek] = useState(() => weekFromQuery(searchParams.get('week')));
    const [cameraFor, setCameraFor] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [queued, setQueued] = useState(new Set());
    const [noteFor, setNoteFor] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [visitNoteFor, setVisitNoteFor] = useState(null);
    const [visitNoteText, setVisitNoteText] = useState('');
    const [editFor, setEditFor] = useState(null);
    const [filesFor, setFilesFor] = useState(null);

    // Надиктовка — тот же общий хук, что в JHA и в заметках таймшита.
    // Поле, чей микрофон нажат, помним в ref: результат распознавания приходит
    // позже, и к этому моменту человек мог уже переключиться на другое поле.
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation,
    } = useDictation({ lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000 });
    const dictationTargetRef = useRef(null);
    const [dictationTarget, setDictationTarget] = useState(null);

    const stopNoteDictation = () => {
        if (isListening) stopDictation();
        dictationTargetRef.current = null;
        setDictationTarget(null);
    };

    const toggleDictation = async (target) => {
        if (!dictationSupported) return;
        if (isListening && dictationTargetRef.current === target) {
            stopNoteDictation();
            return;
        }

        dictationTargetRef.current = target;
        setDictationTarget(target);

        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !dictationTargetRef.current) return;
                // Функциональное обновление: текст из замыкания к приходу
                // результата уже устарел бы, и надиктованное затёрло бы набранное.
                const append = (prev) => (prev ? prev.replace(/\s+$/, '') + ' ' : '') + transcript;
                if (dictationTargetRef.current === 'visit') setVisitNoteText(append);
                if (dictationTargetRef.current === 'day') setNoteText(append);
            },
            onEnd: () => {
                dictationTargetRef.current = null;
                setDictationTarget(null);
            },
        });
    };

    const micAdornment = (target) => dictationSupported && (
        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
            <IconButton
                onClick={() => toggleDictation(target)}
                color={isListening && dictationTarget === target ? 'error' : 'default'}
                size="small"
                title={isListening && dictationTarget === target ? 'Stop recording' : 'Start voice input'}
            >
                {isListening && dictationTarget === target ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
        </InputAdornment>
    );
    const [addLocationFor, setAddLocationFor] = useState(null);
    const [pickedLocation, setPickedLocation] = useState(null);

    // Что человек сам открыл или закрыл. Храним только его решения, а не все
    // дни: правило по умолчанию должно продолжать работать для новых дней.
    const [dayOverrides, setDayOverrides] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('safetyScheduleOpenDays') || '{}');
        } catch (e) {
            return {};
        }
    });

    const toggleDay = (workDate, isOpen) => {
        setDayOverrides((prev) => {
            const next = { ...prev, [workDate]: !isOpen };
            try {
                localStorage.setItem('safetyScheduleOpenDays', JSON.stringify(next));
            } catch (e) {
                // Приватный режим — состояние проживёт до перезагрузки, и ладно.
            }
            return next;
        });
    };
    const [dailyNotes, setDailyNotes] = useState({});
    const [dailyNoteErrors, setDailyNoteErrors] = useState({});

    // Сегодня — по часам планшета и с перерасчётом: экран на айпаде живёт сутками,
    // и открытый вечером в пятницу в понедельник считал бы сегодняшним пятницу.
    const [today, setToday] = useState(() => todayKey());
    useEffect(() => {
        const tick = () => setToday((prev) => (prev === todayKey() ? prev : todayKey()));
        const timer = setInterval(tick, 60000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, []);
    const currentWeek = useMemo(() => weekEndingOf(today), [today]);
    const selectedWeek = week || currentWeek;

    // Записывать визиты может любой, у кого логин привязан к карточке сотрудника.
    // Сведений ещё нет (первый запуск, старый сервер) — не запрещаем.
    const canRecord = meta ? meta.canRecord !== false : true;

    // Недели в выпадашке. Эскиз просит «Curr» с заглядыванием на 6 недель вперёд.
    const weeks = useMemo(() => {
        // Все шесть недель вперёд, даже пустые: на эскизе «allow to look ahead
        // 6 weeks» без оговорок, и «на эту неделю мне ничего не поставили» —
        // такой же ответ, как список площадок.
        const set = new Set();
        const start = parseDay(currentWeek);
        start.setDate(start.getDate() - 7);
        for (let i = 0; i <= 7; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i * 7);
            set.add(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
        }

        // Плюс недели, где что-то есть, — вдруг расписание уехало дальше.
        days.forEach((d) => set.add(weekEndingOf(d.workDate)));

        // Неделя из адреса — тоже: иначе выпадашка показала бы пустое значение.
        if (week) set.add(week);

        // Сначала текущая, потом вперёд, прошедшие — в конец.
        return orderWeeks(Array.from(set), currentWeek);
    }, [days, currentWeek, week]);

    const selectWeek = (value) => {
        setWeek(value);
        setSearchParams(value === currentWeek ? {} : { week: value }, { replace: true });
    };

    const dayOption = (key) => ({ value: key, label: `${dayTitle(key)}${key === today ? ' — today' : ''}` });

    // Визит добавляют и переносят на сегодня и дальше: прошлое с айпада не правится.
    const visitDays = useMemo(
        () => weekDays(selectedWeek).filter((key) => key >= today).map(dayOption),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedWeek, today]
    );

    // Заметку на день пишут за сегодня и прошедшие дни — о будущем писать нечего.
    const noteDays = useMemo(
        () => weekDays(selectedWeek).filter((key) => key <= today).map(dayOption),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedWeek, today]
    );

    // Все дни выбранной недели, и пустые тоже: у сегодняшнего и будущих — «Add
    // visit», прошедший виден, даже если в нём ничего не было.
    const visibleDays = useMemo(() => {
        const byDate = new Map(days.map((d) => [d.workDate, d.items]));
        return weekDays(selectedWeek).map((date) => ({ workDate: date, items: byDate.get(date) || [] }));
    }, [days, selectedWeek]);

    const load = useCallback(async () => {
        const grouped = await safetyScheduleService.getScheduleByDay();
        const notes = await safetyScheduleService.getDailyNotes();
        const noteErrors = await safetyScheduleService.getDailyNoteErrors();

        // День, где есть только заметка («весь день лил дождь, площадки закрыты»),
        // тоже показываем. Раньше заметку на день без площадок не было видно
        // нигде, и её легко было затереть, открыв окно заметок заново.
        const byDate = new Map(grouped.map((day) => [day.workDate, day]));
        [...Object.keys(notes), ...Object.keys(noteErrors)].forEach((workDate) => {
            if (!byDate.has(workDate)) byDate.set(workDate, { workDate, items: [] });
        });

        setDays(Array.from(byDate.values()).sort((a, b) => a.workDate.localeCompare(b.workDate)));
        setDailyNotes(notes);
        setDailyNoteErrors(noteErrors);
        setMeta(await safetyScheduleService.getMeta());

        setPending(await safetyScheduleService.pendingSyncCount());
        setQueued(await safetyScheduleService.pendingVisitUuids());
        setLoading(false);
    }, []);

    // Площадки для «Add location» — из того же синка, что и в остальных формах.
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();
                const readAll = async (store) => (db.objectStoreNames.contains(store)
                    ? await db.transaction(store, 'readonly').objectStore(store).getAll()
                    : []);
                setLocations(buildLocationOptions(await readAll(JOB_STORE), await readAll(ALT_JOB_STORE)));
            } catch (e) {
                // Без списка экран всё равно работает: локацию можно вписать руками.
                console.warn('[SafetySchedule] could not load locations', e);
            }
        })();
    }, []);

    // При открытии экрана тянем свежее сами: ждать, пока человек сходит в
    // «Sync All Data», неправильно — он открыл именно расписание.
    const refresh = useCallback(async () => {
        setRefreshing(true);
        await safetyScheduleService.refreshFromServer();
        await load();
        setRefreshing(false);
    }, [load]);

    useEffect(() => {
        load();
        refresh();

        const goOnline = () => { setOnline(true); refresh(); };
        const goOffline = () => setOnline(false);

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, [load, refresh]);

    // Пока в очереди что-то есть — повторяем каждые полминуты.
    // Раньше отправка запускалась только по действию, по возврату сети и при
    // открытии экрана: сорвалась первая попытка — и отметка лежала до тех пор,
    // пока человек не сделает что-то ещё. На площадке связь моргает постоянно.
    useEffect(() => {
        if (pending === 0) return undefined;

        const timer = setInterval(async () => {
            if (!navigator.onLine) return;
            await safetyScheduleService.processOutbox();
            setPending(await safetyScheduleService.pendingSyncCount());
            await load();
        }, 30000);

        return () => clearInterval(timer);
    }, [pending, load]);

    // После любой записи: сразу показать то, что легло на айпад, потом дождаться
    // отправки и дочитать счётчики — иначе они показывали бы уже отправленное.
    const afterWrite = async () => {
        await load();
        await safetyScheduleService.processOutbox();
        setPending(await safetyScheduleService.pendingSyncCount());
        await load();
    };

    const patchVisit = async (assignment, changes, options) => {
        try {
            await safetyScheduleService.saveVisit(assignment.id, changes, options);
            await afterWrite();
        } catch (e) {
            Swal.fire('Could not save', e.message || 'Please try again', 'error');
        }
    };

    const stampTime = (assignment, field) => {
        const current = assignment.visit?.[field];
        if (!current) {
            patchVisit(assignment, { [field]: nowTime() });
            return;
        }

        // Время уже стоит — значит его правят, а не ставят заново. Поле time, а
        // не текст: на айпаде оно открывает родное колесо выбора времени вместо
        // клавиатуры и формата «HH:MM», который надо помнить. Clear — если
        // отметку поставили по ошибке: раньше стереть её было нечем.
        Swal.fire({
            title: field === 'arriveAt' ? 'Arrive time' : 'Depart time',
            input: 'time',
            inputValue: current,
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Save',
            denyButtonText: 'Clear',
            preConfirm: (value) => {
                const time = (value || '').trim().slice(0, 5);
                if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
                    Swal.showValidationMessage('Pick a time');
                    return false;
                }
                return time;
            },
        }).then((result) => {
            if (result.isConfirmed) patchVisit(assignment, { [field]: result.value });
            if (result.isDenied) patchVisit(assignment, {}, { clear: [field] });
        });
    };

    // Диалог, а не Swal: в поле Swal не поставить кнопку надиктовки, а по
    // всему приложению заметки надиктовываются.
    const editNotes = (assignment) => {
        setVisitNoteFor(assignment);
        setVisitNoteText(assignment.visit?.notes || '');
    };

    const closeVisitNote = () => {
        stopNoteDictation();
        setVisitNoteFor(null);
    };

    const confirmVisitNote = () => {
        const assignment = visitNoteFor;
        const text = (visitNoteText || '').trim();
        closeVisitNote();
        if (assignment) patchVisit(assignment, { notes: text || null });
    };

    // Выбор площадки — тем же Autocomplete, что и в форме инспекции: свой
    // Swal с datalist здесь не к месту, в приложении для этого есть свой способ.
    const picked = useMemo(() => pickLocation(pickedLocation, locations), [pickedLocation, locations]);

    const openAddLocation = (workDate) => {
        setPickedLocation(null);
        setAddLocationFor(workDate);
    };

    const confirmAddLocation = async () => {
        if (!picked.label) return;

        const workDate = addLocationFor;
        setAddLocationFor(null);
        setPickedLocation(null);

        try {
            await safetyScheduleService.addOwnLocation(workDate, picked.label, picked.jobCodes);
            await afterWrite();
        } catch (e) {
            Swal.fire('Could not add the site', e.message || 'Please try again', 'error');
        }
    };

    // Убрать свою площадку — сегодняшнюю или будущую. Назначенное офисом и
    // прошедшее отсюда не убирается.
    const removeVisit = async (assignment) => {
        const result = await Swal.fire({
            title: 'Remove this visit?',
            text: `${assignment.location} · ${dayTitle(assignment.workDate)}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            confirmButtonColor: '#d33',
        });
        if (!result.isConfirmed) return;

        try {
            await safetyScheduleService.removeOwnVisit(assignment.id);
            await afterWrite();
        } catch (e) {
            Swal.fire('Could not remove the visit', e.message || 'Please try again', 'error');
        }
    };

    const saveEdit = async (changes) => {
        const assignment = editFor;
        setEditFor(null);
        try {
            await safetyScheduleService.updateOwnVisit(assignment.id, changes);
            await afterWrite();
        } catch (e) {
            Swal.fire('Could not save', e.message || 'Please try again', 'error');
        }
    };

    const openDailyNote = (workDate) => {
        setNoteFor(workDate);
        setNoteText(dailyNotes[workDate] || '');
    };

    // День меняется прямо в диалоге — как и при добавлении площадки. Раньше это
    // был Swal без единого упоминания дня: сохранил и гадай, куда попало.
    const changeNoteDay = (workDate) => {
        stopNoteDictation();
        setNoteFor(workDate);
        setNoteText(dailyNotes[workDate] || '');
    };

    const closeDailyNote = () => {
        stopNoteDictation();
        setNoteFor(null);
    };

    const confirmDailyNote = async () => {
        const workDate = noteFor;
        const text = noteText || '';

        closeDailyNote();
        await safetyScheduleService.saveDailyNote(workDate, text);
        setDailyNotes((prev) => ({ ...prev, [workDate]: text }));
        await afterWrite();
    };

    const attachFile = async (assignment, file, errorTitle) => {
        try {
            await safetyScheduleService.addFile(assignment.id, file);
            await afterWrite();
        } catch (e) {
            Swal.fire(errorTitle, e.message || 'Please try again', 'error');
        }
    };

    const takePhoto = async (photo) => {
        const assignment = cameraFor;
        setCameraFor(null);
        if (!assignment || !photo?.dataUrl) return;
        await attachFile(assignment, { dataUrl: await downscalePhoto(photo.dataUrl) }, 'Could not add the photo');
    };

    // «Ability to Add Pictures or Documents» — документ выбирается из файлов
    // айпада (разрешение, письмо, схема). Фото для этого есть кнопка Photo.
    const documentInputRef = useRef(null);
    const [documentFor, setDocumentFor] = useState(null);

    const pickDocument = (assignment) => {
        setDocumentFor(assignment);
        if (documentInputRef.current) {
            documentInputRef.current.value = '';
            documentInputRef.current.click();
        }
    };

    const onDocumentPicked = async (event) => {
        const file = event.target.files && event.target.files[0];
        const assignment = documentFor;
        setDocumentFor(null);
        if (!file || !assignment) return;

        if (file.type !== 'application/pdf') {
            Swal.fire('Not a PDF', 'Pick a PDF document. Use Photo for pictures.', 'warning');
            return;
        }
        if (file.size > MAX_DOCUMENT_BYTES) {
            Swal.fire('File is too large', `Documents up to ${Math.round(MAX_DOCUMENT_BYTES / 1024 / 1024)} MB can be attached.`, 'warning');
            return;
        }

        try {
            const dataUrl = await readAsDataUrl(file);
            await attachFile(assignment, { dataUrl, name: file.name }, 'Could not add the document');
        } catch (e) {
            Swal.fire('Could not add the document', e.message || 'Please try again', 'error');
        }
    };

    const openInspection = async (assignment) => {
        // Форма инспекции умеет принимать только ?uuid= и подтягивает черновик из
        // IndexedDB. Поэтому черновик с уже заполненной площадкой заводим здесь,
        // а форму открываем по нему — иначе рэп набивал бы локацию второй раз.
        try {
            const result = await safetyScheduleService.startInspection(assignment);

            // Сданную инспекцию второй раз не открываем: на планшете её уже нет,
            // форма открылась бы пустой, и повторная сдача переписала бы первую.
            if (result.submitted) {
                const score = result.scorePercent;
                Swal.fire({
                    icon: 'success',
                    title: 'Inspection submitted',
                    text: score !== null && score !== undefined
                        ? `This site's inspection is in with a score of ${Number(score)}%.`
                        : 'This site\'s inspection is already in.',
                });
                return;
            }

            setPending(await safetyScheduleService.pendingSyncCount());
            navigate(`/safety/forms/job-site-safety-inspection?uuid=${encodeURIComponent(result.uuid)}`);
        } catch (e) {
            Swal.fire('Could not open the inspection', e.message || 'Please try again', 'error');
        }
    };

    // Визит — строка внутри карточки своего дня: день читается одним блоком.
    const renderVisit = (assignment, isToday, isPast) => {
        const visit = assignment.visit || {};
        const score = visit.scorePercent;
        const hasScore = score !== null && score !== undefined;
        // Зелёная — когда инспекция сдана. По флагу, а не только по счёту:
        // инспекция, где все ответы N/A, сдана, но процента у неё нет.
        const inspectionDone = Boolean(visit.inspectionSubmitted) || hasScore;
        // Красная кнопка, пока инспекции нет, — и только для наступивших дней:
        // за будущее краснеть не за что.
        const overdue = !inspectionDone && (isToday || isPast);
        const photoCount = visit.photoCount || 0;
        // Доступность дня — по часам планшета, а не по флагу из синка: флаг,
        // посчитанный вчера вечером, утром без связи держал бы сегодняшние
        // карточки закрытыми. «Only allow SSI for current day or prior».
        const editable = assignment.workDate <= today && canRecord && !assignment.pendingDelete;
        // Свою площадку на сегодня и дальше можно перенести или убрать.
        const ownEditable = canRecord && safetyScheduleService.isOwnEditable(assignment);

        // Три состояния, а не два: «ничего не отмечал» — это не «не отправлено».
        // Значок появляется только когда человек уже что-то ввёл.
        const touched = Boolean(visit.uuid && (visit.arriveAt || visit.departAt || visit.notes || photoCount));
        const sync = visit.syncError ? 'failed' : (queued.has(visit.uuid) ? 'queued' : (touched ? 'sent' : null));

        return (
            <Box key={assignment.id} sx={{ px: 1.5, py: 1.25, opacity: assignment.pendingDelete ? 0.55 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {sync === 'sent' && (
                        <SentIcon titleAccess="Saved on the server" sx={{ fontSize: 18, color: '#4caf50' }} />
                    )}
                    {sync === 'queued' && (
                        <QueuedIcon titleAccess="Saved on this iPad, waiting to send" sx={{ fontSize: 18, color: '#ff9800' }} />
                    )}
                    {sync === 'failed' && (
                        <NotSentIcon titleAccess="Did not save" sx={{ fontSize: 18, color: '#c62828' }} />
                    )}
                    <Typography variant="subtitle1" sx={{ fontSize: '0.95rem', flex: 1 }}>
                        {assignment.location}
                    </Typography>
                    {assignment.addedByRep && (
                        <Chip
                            label="Added by you"
                            size="small"
                            sx={{ backgroundColor: '#9e9e9e', color: 'white', height: 20 }}
                        />
                    )}
                    {hasScore && (
                        <Chip
                            label={`${Number(score)}%`}
                            size="small"
                            sx={{
                                backgroundColor: Number(score) >= 90 ? '#4caf50' : Number(score) >= 70 ? '#ff9800' : '#f44336',
                                color: 'white',
                                height: 20,
                            }}
                        />
                    )}
                    {/* Свою площадку на сегодня и дальше можно поправить или убрать.
                        Назначенные офисом и прошедшие — нет. */}
                    {ownEditable && (
                        <>
                            <IconButton
                                size="small"
                                onClick={() => setEditFor(assignment)}
                                title="Change this visit"
                                aria-label={`Change ${assignment.location}`}
                                sx={{ ml: -0.5 }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => removeVisit(assignment)}
                                title="Remove this visit"
                                aria-label={`Remove ${assignment.location}`}
                                sx={{ ml: -0.5, color: '#d32f2f' }}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </Box>

                {assignment.pendingDelete && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {online ? 'Removing…' : 'Will be removed when you are back online.'}
                    </Typography>
                )}

                {visit.syncError && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#c62828' }}>
                        <strong>Not saved:</strong> {visit.syncError}
                    </Typography>
                )}

                {assignment.scheduleNotes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        <strong>Note:</strong> {assignment.scheduleNotes}
                    </Typography>
                )}

                {visit.notes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        <strong>Your notes:</strong> {visit.notes}
                    </Typography>
                )}

                {/* Будущий день: вместо ряда серых кнопок, на которые ничего не
                    нажать, — одна строка, когда они откроются. */}
                {assignment.workDate > today ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Arrive, SSI and photos open on that day.
                    </Typography>
                ) : (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Button
                        size="small"
                        variant={visit.arriveAt ? 'contained' : 'outlined'}
                        startIcon={<ArriveIcon />}
                        disabled={!editable}
                        onClick={() => stampTime(assignment, 'arriveAt')}
                        sx={visit.arriveAt ? { backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } } : undefined}
                    >
                        {visit.arriveAt ? formatTime(visit.arriveAt) : 'Arrive'}
                    </Button>

                    <Button
                        size="small"
                        variant={visit.departAt ? 'contained' : 'outlined'}
                        startIcon={<DepartIcon />}
                        disabled={!editable}
                        onClick={() => stampTime(assignment, 'departAt')}
                        sx={visit.departAt ? { backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } } : undefined}
                    >
                        {visit.departAt ? formatTime(visit.departAt) : 'Depart'}
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!editable}
                        onClick={() => editNotes(assignment)}
                    >
                        Notes
                    </Button>

                    <Button
                        size="small"
                        variant={overdue || inspectionDone ? 'contained' : 'outlined'}
                        color={overdue ? 'error' : 'primary'}
                        startIcon={<InspectionIcon />}
                        disabled={!editable}
                        onClick={() => openInspection(assignment)}
                        sx={inspectionDone ? { backgroundColor: '#4caf50', color: 'white', '&:hover': { backgroundColor: '#45a049' } } : undefined}
                    >
                        SSI
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhotoIcon />}
                        disabled={!editable || photoCount >= MAX_FILES_PER_VISIT}
                        onClick={() => setCameraFor(assignment)}
                    >
                        Photo
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DocumentIcon />}
                        disabled={!editable || photoCount >= MAX_FILES_PER_VISIT}
                        onClick={() => pickDocument(assignment)}
                    >
                        Document
                    </Button>

                    {/* Что приложено — каждый снимок и документ отдельно. */}
                    {photoCount > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilesIcon />}
                            onClick={() => setFilesFor(assignment)}
                        >
                            {`Files ${photoCount}/${MAX_FILES_PER_VISIT}`}
                        </Button>
                    )}
                </Stack>
                )}
            </Box>
        );
    };

    const renderWeekNotice = () => {
        if (meta && meta.canRecord === false) {
            return (
                <Card sx={{ p: 1.5, backgroundColor: '#fff3e0' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <InfoIcon sx={{ color: '#ef6c00', fontSize: 20, mt: '1px' }} />
                        <Typography variant="body2">
                            {meta.cannotRecordReason || 'Visits cannot be recorded from this login.'}
                        </Typography>
                    </Box>
                </Card>
            );
        }

        return null;
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>My Schedule</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Расписание всей команды на ту же неделю — только просмотр. */}
                        <Button
                            variant="outlined"
                            startIcon={<GroupViewIcon />}
                            onClick={() => navigate(`/safety/schedule/group?week=${selectedWeek}`)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Group View
                        </Button>
                        <TextField
                            select
                            size="small"
                            value={selectedWeek}
                            onChange={(e) => selectWeek(e.target.value)}
                            sx={{ minWidth: 190 }}
                        >
                            {weeks.map((w) => (
                                <MenuItem key={w} value={w}>{weekLabel(w, currentWeek)}</MenuItem>
                            ))}
                        </TextField>
                        {!online && (
                            <Chip
                                icon={<OfflineIcon sx={{ color: 'white !important' }} />}
                                label="Offline"
                                size="small"
                                sx={{ backgroundColor: '#ff9800', color: 'white', height: 24 }}
                            />
                        )}
                        {refreshing && (
                            <Chip
                                icon={<SyncIcon sx={{ color: 'white !important' }} />}
                                label="Updating"
                                size="small"
                                sx={{ backgroundColor: '#9e9e9e', color: 'white', height: 24 }}
                            />
                        )}
                        {pending > 0 && (
                            <Chip
                                icon={<SyncIcon sx={{ color: 'white !important' }} />}
                                label={`${pending} to send`}
                                size="small"
                                sx={{ backgroundColor: '#2196f3', color: 'white', height: 24 }}
                            />
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : (
                    <Stack spacing={1.5}>
                        {!online && (
                            <Card sx={{ p: 1.5, backgroundColor: '#fff8e1' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No connection. Everything you tap is saved on this iPad and sent when you are back online.
                                </Typography>
                            </Card>
                        )}

                        {renderWeekNotice()}

                        {visibleDays.map(({ workDate, items }) => {
                            const date = parseDay(workDate);
                            const isToday = workDate === today;
                            const isPast = workDate < today;

                            // Прошедшие дни по умолчанию свёрнуты — иначе неделя
                            // растягивается и сегодняшний день тонет. Решение
                            // человека, если он открыл или закрыл сам, важнее.
                            const isOpen = dayOverrides[workDate] !== undefined
                                ? dayOverrides[workDate]
                                : !isPast;

                            // У свёрнутого дня должно быть видно, осталось ли там
                            // несделанное: ради этого прошлые дни и показываются.
                            const openSsi = items.filter((a) => {
                                const sc = a.visit?.scorePercent;
                                const done = Boolean(a.visit?.inspectionSubmitted) || (sc !== null && sc !== undefined);
                                return !done && (isToday || isPast);
                            }).length;

                            // Раскрывать есть что — визиты или заметка на день.
                            // Пустой день — одна строка: день и кнопка.
                            const hasBody = items.length > 0
                                || Boolean(dailyNotes[workDate])
                                || Boolean(dailyNoteErrors[workDate]);
                            const expanded = hasBody && isOpen;

                            // Каждый день — своя карточка: неделя читается как
                            // список дней, а не как россыпь строк на сером фоне.
                            // overflow visible — иначе шапка не прилипнет.
                            return (
                                <Card key={workDate} sx={{ overflow: 'visible' }}>
                                    {/* Шапка дня прилипает к верху списка: на дне с
                                        пятью площадками иначе не видно, к какому дню
                                        ты прокрутил, а кнопки уезжают наверх. Кнопки
                                        дня видны и у свёрнутого дня. */}
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{
                                            position: 'sticky', top: 0, zIndex: 2,
                                            backgroundColor: 'white', borderRadius: 1,
                                            px: 1.5, py: 1, minHeight: 52,
                                        }}
                                    >
                                        <Box
                                            onClick={hasBody ? () => toggleDay(workDate, isOpen) : undefined}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1,
                                                cursor: hasBody ? 'pointer' : 'default', userSelect: 'none',
                                                flexGrow: 1, minWidth: 0, alignSelf: 'stretch',
                                            }}
                                        >
                                            <ExpandMoreIcon
                                                sx={{
                                                    fontSize: 22, color: 'text.secondary',
                                                    visibility: hasBody ? 'visible' : 'hidden',
                                                    transform: expanded ? 'none' : 'rotate(-90deg)',
                                                    transition: 'transform .15s',
                                                }}
                                            />
                                            <Typography variant="subtitle1" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                                {DOW[date.getDay()]} {date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}
                                            </Typography>
                                            {isToday && (
                                                <Chip
                                                    label="Today"
                                                    size="small"
                                                    sx={{ backgroundColor: '#4caf50', color: 'white', height: 20 }}
                                                />
                                            )}
                                            {!expanded && openSsi > 0 && (
                                                <Chip
                                                    label={`${openSsi} SSI left`}
                                                    size="small"
                                                    sx={{ backgroundColor: '#f44336', color: 'white', height: 20 }}
                                                />
                                            )}
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                {items.length === 0
                                                    ? 'No visits'
                                                    : `${items.length} ${items.length === 1 ? 'visit' : 'visits'}`}
                                            </Typography>
                                        </Box>

                                        {canRecord && !isPast && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddLocationIcon />}
                                                onClick={() => openAddLocation(workDate)}
                                            >
                                                Add visit
                                            </Button>
                                        )}
                                        {/* Зелёная, когда заметка на день уже есть, —
                                            как Arrive/Depart на карточках. */}
                                        {canRecord && (isToday || isPast) && (
                                            <Button
                                                size="small"
                                                variant={dailyNotes[workDate] ? 'contained' : 'outlined'}
                                                startIcon={<DailyNoteIcon />}
                                                onClick={() => openDailyNote(workDate)}
                                                sx={dailyNotes[workDate]
                                                    ? { backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }
                                                    : undefined}
                                            >
                                                Daily notes
                                            </Button>
                                        )}
                                    </Stack>

                                    {hasBody && (
                                        <Collapse in={isOpen} unmountOnExit>
                                            <Divider />
                                            {(dailyNotes[workDate] || dailyNoteErrors[workDate]) && (
                                                <Box sx={{ px: 1.5, pt: 1 }}>
                                                    {dailyNotes[workDate] && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            <strong>Day notes:</strong> {dailyNotes[workDate]}
                                                        </Typography>
                                                    )}
                                                    {dailyNoteErrors[workDate] && (
                                                        <Typography variant="caption" display="block" sx={{ color: '#c62828' }}>
                                                            <strong>Not saved:</strong> {dailyNoteErrors[workDate]}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                            {items.length === 0 && <Box sx={{ pb: 1 }} />}

                                            {items.map((assignment, index) => (
                                                <React.Fragment key={assignment.id}>
                                                    {index > 0 && <Divider sx={{ mx: 1.5 }} />}
                                                    {renderVisit(assignment, isToday, isPast)}
                                                </React.Fragment>
                                            ))}
                                        </Collapse>
                                    )}
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </Box>
            <Dialog
                open={Boolean(addLocationFor)}
                onClose={() => setAddLocationFor(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add a visit</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A site that is not on your schedule — for today or a day ahead.
                    </Typography>

                    {/* День подставляется тот, под которым нажали, но его видно и
                        можно поменять — иначе непонятно, куда всё уедет. */}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Day"
                        value={addLocationFor || ''}
                        onChange={(e) => setAddLocationFor(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        {visitDays.map((d) => (
                            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                    </TextField>

                    <Autocomplete
                        freeSolo
                        autoFocus
                        options={locations}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                        value={pickedLocation}
                        onChange={(e, v) => setPickedLocation(v)}
                        onInputChange={(e, v, reason) => { if (reason === 'input') setPickedLocation(v); }}
                        renderInput={(params) => (
                            <TextField {...params} size="small" autoFocus
                                       label="Location"
                                       placeholder="Start typing to find a job site" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddLocationFor(null)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmAddLocation}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <VisitEditDialog
                open={Boolean(editFor)}
                assignment={editFor}
                days={visitDays}
                locations={locations}
                onClose={() => setEditFor(null)}
                onSave={saveEdit}
            />

            <VisitFilesDialog
                open={Boolean(filesFor)}
                assignment={filesFor}
                online={online}
                onClose={() => setFilesFor(null)}
            />

            <Dialog
                open={Boolean(noteFor)}
                onClose={closeDailyNote}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Notes for the day</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Anything about the whole day, not one site.
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Day"
                        value={noteFor || ''}
                        onChange={(e) => changeNoteDay(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        {noteDays.map((d) => (
                            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label="Notes"
                        placeholder={dictationSupported ? 'Type or dictate notes for the day...' : 'Notes for the day...'}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        InputProps={{ endAdornment: micAdornment('day') }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDailyNote}>Cancel</Button>
                    <Button variant="contained" onClick={confirmDailyNote}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(visitNoteFor)}
                onClose={closeVisitNote}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Notes</DialogTitle>
                <DialogContent>
                    {/* Площадка и день — сверху: окно одинаковое для всех карточек,
                        и без этого не видно, к какому визиту пишешь. */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {visitNoteFor?.location}
                        {visitNoteFor?.workDate ? ` · ${dayTitle(visitNoteFor.workDate)}` : ''}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label="Notes"
                        placeholder={dictationSupported ? 'Type or dictate notes for this site...' : 'Notes for this site...'}
                        value={visitNoteText}
                        onChange={(e) => setVisitNoteText(e.target.value)}
                        autoFocus
                        InputProps={{ endAdornment: micAdornment('visit') }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeVisitNote}>Cancel</Button>
                    <Button variant="contained" onClick={confirmVisitNote}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <CameraCapture
                open={Boolean(cameraFor)}
                onClose={() => setCameraFor(null)}
                onCapture={takePhoto}
            />

            <input
                ref={documentInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={onDocumentPicked}
            />
        </Box>
    );
};

export default SafetySchedulePage;
